import { validator } from "./validators.js";
const sameValue = (a, b) => JSON.stringify(a) === JSON.stringify(b);
/** Class of one catalog command for a value (set: the parameter's value; action: args[escalate.when.arg]). */
export function commandClassOf(command, value) {
    return command.escalate && sameValue(value, command.escalate.when.equals) ? command.escalate.actionClass : command.actionClass;
}
/** Rule of a class from the catalog (TTL limit, clock requirement, repeat rule). */
export function actionClassRule(catalog, name) {
    const rule = catalog.actionClasses.find((c) => c.class === name);
    if (!rule)
        throw new Error(`action class ${name} missing in the catalog`);
    return rule;
}
/**
 * Action class of a whole command (README "Aktionsklassen"): an action takes its class, stricter if its argument
 * escalates it (backflush.set armed=true, standby.set on=false); a set takes the strictest class of its values
 * (pid.enabled=true, standby.enabled=false, brew.warmup_flush=true, schedule.enabled=true arm heating, waking or water).
 * undefined if a name is not in the catalog.
 */
export function effectiveActionClass(cmd, catalog) {
    if (cmd.kind === "action") {
        const command = findCommand(catalog, cmd.action);
        if (!command)
            return undefined;
        const arg = command.escalate?.when.arg;
        return actionClassRule(catalog, commandClassOf(command, arg === undefined ? undefined : cmd.args?.[arg]));
    }
    let strictest;
    for (const [id, value] of Object.entries(cmd.params)) {
        const command = findCommand(catalog, id);
        if (!command)
            return undefined;
        const rule = actionClassRule(catalog, commandClassOf(command, value));
        if (!strictest || rule.rank > strictest.rank)
            strictest = rule;
    }
    return strictest;
}
/** UTF-8 length without TextEncoder (works without DOM or Node typings). */
export function utf8Length(text) {
    let bytes = 0;
    for (const ch of text) {
        const cp = ch.codePointAt(0) ?? 0;
        bytes += cp < 0x80 ? 1 : cp < 0x800 ? 2 : cp < 0x10000 ? 3 : 4;
    }
    return bytes;
}
export const findCommand = (catalog, id) => catalog.commands.find((c) => c.id === id);
function roleAllows(command, role) {
    if (command.role === "system")
        return role === "system";
    if (command.role === "owner" || command.protection === "S")
        return role === "owner" || role === "system";
    return true;
}
/** Checks one value like the firmware does (POST /parameters): type, options, length; numbers are clamped. */
function checkValue(command, value) {
    switch (command.type) {
        case "bool":
            return typeof value === "boolean" ? { ok: true, value, clamped: false } : { ok: false, reason: "invalid_value" };
        case "string": {
            if (typeof value !== "string")
                return { ok: false, reason: "invalid_value" };
            if (value === "" && !command.allowEmpty)
                return { ok: false, reason: "invalid_value" };
            if (command.maxLength !== undefined && utf8Length(value) > command.maxLength)
                return { ok: false, reason: "too_long" };
            return { ok: true, value, clamped: false };
        }
        case "enum":
            return typeof value === "number" && (command.options ?? []).some((o) => o.value === value) ? { ok: true, value, clamped: false } : { ok: false, reason: "invalid_value" };
        case "integer":
        case "number": {
            if (typeof value !== "number" || !Number.isFinite(value) || (command.type === "integer" && !Number.isInteger(value)))
                return { ok: false, reason: "invalid_value" };
            const lo = command.min ?? -Infinity;
            const hi = command.max ?? Infinity;
            const v = Math.min(hi, Math.max(lo, value));
            return { ok: true, value: v, clamped: v !== value };
        }
        default:
            return { ok: false, reason: "unknown_param" };
    }
}
const argsCache = new WeakMap();
/**
 * Checks a command against a machine's command catalog the way the firmware will (unknown command or parameter, type,
 * length, options, clamping, args schema, expect keys) plus the cloud's role rules (owner only, protection class S,
 * system commands) and the rules of the action class (TTL limit, clock). The firmware stays the last instance (NFR-1);
 * this check lets the API refuse early with the same reason codes, and the software machine answer like the firmware.
 * A ttl above the class limit is refused here (the firmware would shorten the validity to the limit), as is a sent
 * later than intentAt (the API sets sent = min(intentAt, receipt)).
 */
export function checkCommand(cmd, catalog, options = {}) {
    const role = options.role ?? "owner";
    if (cmd.intentAt !== undefined && cmd.sent > cmd.intentAt)
        return { ok: false, reason: "invalid_value", detail: "sent must be min(intentAt, receipt)" };
    const actionClass = effectiveActionClass(cmd, catalog);
    if (cmd.kind === "set") {
        const effective = {};
        const clamped = [];
        for (const [id, value] of Object.entries(cmd.params)) {
            const command = findCommand(catalog, id);
            if (!command || command.kind !== "set" || value === undefined)
                return { ok: false, reason: "unknown_param", param: id };
            if (!roleAllows(command, role))
                return { ok: false, reason: "owner_only", param: id };
            const checked = checkValue(command, value);
            if (!checked.ok)
                return { ok: false, reason: checked.reason, param: id };
            effective[id] = checked.value;
            if (checked.clamped)
                clamped.push(id);
        }
        const classProblem = checkClass(cmd, actionClass, options);
        return classProblem ?? { ok: true, command: cmd, actionClass: actionClass, clamped, effective };
    }
    const command = findCommand(catalog, cmd.action);
    if (!command || command.kind !== "action")
        return { ok: false, reason: "unknown_command" };
    if (!roleAllows(command, role))
        return { ok: false, reason: command.role === "system" ? "forbidden" : "owner_only" };
    if (cmd.part && !command.multipart)
        return { ok: false, reason: "invalid_value", detail: "not a multi-part command" };
    for (const key of command.expect ?? []) {
        if (!cmd.expect || !(key in cmd.expect))
            return { ok: false, reason: "invalid_value", detail: `expect must name ${key}` };
    }
    if (command.args && !cmd.part) {
        let fn = argsCache.get(command.args);
        if (!fn) {
            fn = (options.validator ?? validator()).compile(command.args);
            argsCache.set(command.args, fn);
        }
        if (!fn(cmd.args ?? {}))
            return { ok: false, reason: "invalid_value", detail: (fn.errors ?? []).map((e) => `${e.instancePath || "args"} ${e.message}`).join("; ") };
    }
    return checkClass(cmd, actionClass, options) ?? { ok: true, command: cmd, actionClass: actionClass, clamped: [] };
}
function checkClass(cmd, actionClass, options) {
    if (!actionClass)
        return { ok: false, reason: "unknown_command" };
    if (cmd.ttl > actionClass.ttl)
        return { ok: false, reason: "invalid_value", detail: `ttl above ${actionClass.ttl} s of action class ${actionClass.class}` };
    if (actionClass.clock && options.clockValid === false)
        return { ok: false, reason: "no_clock" };
    return undefined;
}
//# sourceMappingURL=commands.js.map